import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@execonline-inc/execonline-ui';
import { useQueryClient } from '@tanstack/react-query';
import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { NotTranslated, T } from '../../../Translations';
import { deleteConnection, serverErrorMessage } from '../Api';
import { ConnectionResource, SubmitFailure } from '../Types';

interface Props {
  resource: ConnectionResource;
}

type State = { kind: 'idle' } | { kind: 'confirming'; error: SubmitFailure } | { kind: 'deleting' };

const DeleteConnection: React.FC<Props> = ({ resource }) => {
  const [state, setState] = React.useState<State>({ kind: 'idle' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const performDelete = () => {
    setState({ kind: 'deleting' });
    deleteConnection(resource.links).fork(
      (err) => setState({ kind: 'confirming', error: just(serverErrorMessage(err)) }),
      () => {
        queryClient.invalidateQueries({ queryKey: ['sso-configurations'] });
        navigate('/single_sign_on');
      },
    );
  };

  return (
    <>
      <Button
        color="error"
        variant="text"
        className="px-0"
        onClick={() => setState({ kind: 'confirming', error: nothing() })}
        data-intercom-target="sso-delete-connection-button"
      >
        <T kind="Delete this connection" />
      </Button>
      <Modal
        size="md"
        open={state.kind === 'confirming' || state.kind === 'deleting'}
        onClose={() => setState({ kind: 'idle' })}
      >
        <ModalHeader>
          <T kind="Are you sure?" />
        </ModalHeader>
        <ModalBody>
          <p className="text-sm text-muted-foreground">
            <T kind="Deleting removes this connection from ExecOnline. Unassign its domains first - people on them return to password sign-in." />
          </p>
          {state.kind === 'confirming' &&
            state.error
              .map((message) => (
                <p key="error" className="mt-3 text-sm text-destructive">
                  {message
                    .map((text) => <NotTranslated key="server" text={text} />)
                    .getOrElse(() => (
                      <T kind="We are unable to process your request at this time" />
                    ))}
                </p>
              ))
              .getOrElse(() => <></>)}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" variant="outlined" onClick={() => setState({ kind: 'idle' })}>
            <T kind="Cancel" />
          </Button>
          <Button
            color="error"
            isLoading={state.kind === 'deleting'}
            onClick={performDelete}
            data-intercom-target="sso-delete-modal-confirm-button"
          >
            <T kind="Delete this connection" />
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default observer(DeleteConnection);
